import * as React from 'react';

import { AuthProvider } from '@40seas/entities/auth';
import { isReevolHostname, getResolvedUrls } from '@40seas/frontend/libs/configs/consts';
import { useDashboardToken } from '@40seas/frontend/dashboard/hooks';
import { useSetupData, useAuth0ContextValue } from '@40seas/frontend/libs/hooks';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import { CentralizedAnimatedLoader } from '@40seas/payments-shared';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import jwtDecode from 'jwt-decode';
import { UserProviderGuest } from './UserProviderGuest';
import { UserProviderWithAuthenticationNew } from './UserProviderWithAuthenticationNew';
import { SafeAuth0Provider } from './SafeAuth0Provider';
import { AuthProvider as AuthV2Provider, getAuthProvider, useAuth as useAuthV2 } from '@40seas/auth-client';

const shouldUseGuestProvider = (isAuthenticated: boolean, guestToken: string | null): boolean => {
    return (
        !isAuthenticated &&
        !window.location.pathname.endsWith('/signup') &&
        !window.location.pathname.endsWith('/login') &&
        !!guestToken &&
        jwtDecode<{ guestPortal: boolean }>(guestToken).guestPortal
    );
};

export const AuthenticationProvider = ({ children }: { children: React.ReactNode }) => {
    const setup = useSetupData();
    const authProvider = getAuthProvider();

    if (authProvider === AuthProvider.SelfManaged) {
        return (
            <AuthV2Provider onRedirectCallback={onRedirectCallback}>
                <UserProviderSelectorSelfManaged>{children}</UserProviderSelectorSelfManaged>
            </AuthV2Provider>
        );
    }

    return (
        <Auth0Provider
            cookieDomain={isReevolHostname() ? getResolvedUrls().COOKIE_DOMAIN : process.env.COOKIE_DOMAIN}
            domain={setup.auth0!.domain}
            clientId={setup.auth0!.clientId}
            onRedirectCallback={onRedirectCallback}
            redirectUri={getRedirectUrl()}
            useRefreshTokens={true}
        >
            <UserProviderSelectorAuth0>{children}</UserProviderSelectorAuth0>
        </Auth0Provider>
    );
};

const UserProviderSelectorSelfManaged = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuthV2();
    const { guestToken } = useDashboardToken();

    // Expose self-managed auth context through SafeAuth0Provider for useSafeAuth0 hook
    // This allows shared components (useInit, usePaymentContext, etc.) to work with both auth providers
    const safeAuth0ContextValue = React.useMemo(
        () => ({
            isAuthenticated: auth.isAuthenticated,
            isLoading: auth.isLoading,
            // Map AuthUser to Auth0 User format (sub instead of id)
            // Convert null to undefined for Auth0 compatibility
            user: auth.user
                ? {
                      sub: auth.user.id,
                      email: auth.user.email,
                      name: auth.user.name ?? undefined,
                      picture: auth.user.picture ?? undefined,
                  }
                : undefined,
            loginWithRedirect: auth.loginWithRedirect,
            loginWithPopup: auth.loginWithRedirect, // Use same redirect flow for popup
            logout: auth.logout,
            getAccessTokenSilently: auth.getAccessTokenSilently,
        }),
        [auth.isAuthenticated, auth.isLoading, auth.user, auth.loginWithRedirect, auth.logout, auth.getAccessTokenSilently],
    );

    if (auth.isLoading) {
        return <CentralizedAnimatedLoader />;
    }

    const content = shouldUseGuestProvider(auth.isAuthenticated, guestToken) ? (
        <UserProviderGuest token={guestToken!}>{children}</UserProviderGuest>
    ) : (
        <UserProviderWithAuthenticationNew token={guestToken ?? undefined}>{children}</UserProviderWithAuthenticationNew>
    );

    return <SafeAuth0Provider value={safeAuth0ContextValue}>{content}</SafeAuth0Provider>;
};

const UserProviderSelectorAuth0 = ({ children }: { children: React.ReactNode }) => {
    const auth0 = useAuth0();
    const { guestToken } = useDashboardToken();
    const auth0ContextValue = useAuth0ContextValue(auth0);

    if (auth0.isLoading) {
        return <CentralizedAnimatedLoader />;
    }

    const content = shouldUseGuestProvider(auth0.isAuthenticated, guestToken) ? (
        <UserProviderGuest token={guestToken!}>{children}</UserProviderGuest>
    ) : (
        <UserProviderWithAuthenticationNew token={guestToken ?? undefined}>{children}</UserProviderWithAuthenticationNew>
    );

    return <SafeAuth0Provider value={auth0ContextValue}>{content}</SafeAuth0Provider>;
};

// Auth0 Perist a state before redirect to the login screen, when redirecting back to it we can access it thru the appState
// We can't just redirect back to the returnTo because the same mechanism also work in invitation
// When being invited the returnTo will contain the invitation link, as a result we will retry to validate the invite and we will fail
// As a result, we check if the returnUrl is the invite link, the '/login'. If this is the case we redirect to the required organization
function onRedirectCallback(appState?: any) {
    if (!appState?.returnTo) {
        return;
    }

    const url = new URL('', `${window.location.origin}${appState?.returnTo}`);

    // We are not after an invitation or a direct login, we can redirect to the return url
    if (url.pathname !== '/' && url.pathname !== '/login') {
        datadogRumProvider.setGlobalContext('redirect', appState?.returnTo);

        url.searchParams.delete('code');
        url.searchParams.delete('state');

        window.location.href = url.toString();
    }

    // If we are being redirect after invite, we need to redirect to the organization
    if (url.searchParams.get('organization_name')) {
        datadogRumProvider.setGlobalContext('redirect', `${window.location.origin}/${url.searchParams.get('organization_name')}`);

        window.location.href = `${window.location.origin}/${url.searchParams.get('organization_name')}`;
    }
}

function getRedirectUrl() {
    return `${window.location.origin}${window.location.search}`;
}
